import { throttle } from 'lodash-es';
//@ts-expect-error
import WebSessionCounter from 'web-session-counter';
import { setCookie } from './utils/cookies';
import { getLocationInfo } from './utils/location';
import GenderEstimate from './utils/gender-estimate';
import { globalWindow } from './utils/constants';
import { IS_NATIVE_APP_CLIENT, isValidAppVersion, poll } from './utils/helpers';
import { openDiscountSignupModal } from './templates/_shared/ui-components/DiscountSignupModal/state';
import { InitialUrlSearchParams } from './utils/environment';
import { pushEventToGTMDataLayer } from './utils/gtm-data-layer';
import { openSignInModal } from './templates/_shared/ui-components/SignInModal/utils';
import { fireHeapCustomerCreateEvent, fireHeapCustomerLoginEvent } from './utils/heap-events';
import { initAuthForm } from './templates/_shared/ui-components/AuthForm';
import { removeIneligibleRewardsFromCartAndLLsystem } from './sections/header/Cart/components/Loyalty/loyaltyUtils';
import { ShippingCountryState } from './utils/shipping';
import { shouldUsePersistentCartMode } from './utils/cart/cart-api-utils';
import { prependLocaleToUrl } from './utils/localization';
import { manualLogoutTracking } from './utils/early-logout-detection';

globalWindow.displayDiscountSignupModal = openDiscountSignupModal;
globalWindow.openSignInModal = openSignInModal;

type EmailSignUpRequestBody = {
  email: FormDataEntryValue | null;
  tags: string[];
  accepts_marketing: number;
  attentiveEmailSubscription?: boolean;
};
const setupWebSessionCounter = () => {
  try {
    // Count sessions
    globalWindow.sessionCount = WebSessionCounter.count;
    globalWindow.sessionLastActive = WebSessionCounter.lastActive;
    globalWindow.sessionCurrentUtmCampaign = WebSessionCounter.currentUtmCampaign;
    globalWindow.sessionLastUtmCampaign = WebSessionCounter.lastUtmCampaign;

    // Estimated gender
    globalWindow.estimatedGender = GenderEstimate.estimatedGender; // Returns a gender which is 60% or more of total visits of PDPs/Collections
    globalWindow.genderCountWomen = GenderEstimate.genderCountWomen; // Returns Total count of visits of PDPs/Collections of Women's gender
    globalWindow.genderCountMen = GenderEstimate.genderCountMen; // Returns Total count of visits of PDPs/Collections of Men's gender
  } catch (err) {
    console.error('setupWebCounter failed: ', err);
  }
};

setupWebSessionCounter();

// ECOM-1298: Salesforce Marketing Cloud tracking. If a certain parameter is present, use it to identify the user
((window: Window, document: HTMLDocument) => {
  const params = new URLSearchParams(window.location.search);
  const subscriberKey = params.get('subscriberkey');
  if (!subscriberKey) return;
  const scriptSrc = `https://mcjt890pmhmtwg1s-ytfbzrzx3n4.pub.sfmc-content.com/xiyquqw51qr?subscriberKey=${encodeURIComponent(
    subscriberKey
  )}`;
  //@ts-expect-error
  window.receiveAloIdentity = (email: string) => {
    if (email && email.length >= 6 && email.includes('@')) {
      window.heap.identify(email.toLowerCase());
    }
  };
  const script = document.createElement('script');
  script.defer = true;
  script.src = scriptSrc;
  script.id = 'Alo_Salesforce_SubscriberKey_Identification_Script';
  document.getElementsByTagName('head')[0]?.appendChild(script);
})(window, document);

// ? [REVISIT] what is going on here?
// A11ly show outline on tab press only
//@ts-expect-error // ? [REVISIT]
function handleFirstTab(e) {
  if (e.keyCode === 9) {
    document.body.classList.add('user-is-tabbing');
    globalWindow.removeEventListener('keydown', handleFirstTab);
    globalWindow.addEventListener('mousedown', handleMouseDownOnce);
  }
}
function handleMouseDownOnce() {
  document.body.classList.remove('user-is-tabbing');
  globalWindow.removeEventListener('mousedown', handleMouseDownOnce);
  globalWindow.addEventListener('keydown', handleFirstTab);
}
globalWindow.addEventListener('keydown', handleFirstTab);

// Create cookie if customer sign ups through the footer form / Looks like from email modal too
/* [REVISIT] Bad cookie name, but we're stuck with it. This cookie's presence means "We already have their email" */
export function disableFurtherEmailPrompts() {
  setCookie('modal-email-footer', '365', 365);
}

const contactForm = document.getElementById('contact_form');
contactForm && contactForm.addEventListener('submit', disableFurtherEmailPrompts);

export const runLocationBasedGlobalFunctions = async () => {
  try {
    const locationInfo = await getLocationInfo();
    const countryCode = ShippingCountryState.countryCode ? ShippingCountryState.countryCode : locationInfo.country_code;
    const { isCustomer } = window?.getCustomerInfo?.() || {};
    removeIneligibleRewardsFromCartAndLLsystem(countryCode, isCustomer);
    if (locationInfo.country_code !== 'US') {
      applyNonUSclassToBody();
    }
  } catch (err) {}
};

// Add non-US class to body if location is outside the US
const applyNonUSclassToBody = () => {
  document.body.classList.add('non-US');
};

// Opens signup modal if you ever link to #signup-modal
window.addEventListener(
  'hashchange',
  function () {
    if (window.location.hash === '#signup-modal') {
      setTimeout(() => {
        // put it back so that it can "change" again if user re-clicks link
        window.location.hash = '';
      }, 500);
      if (!IS_NATIVE_APP_CLIENT) return openDiscountSignupModal();
      // native app
      if (isValidAppVersion()) {
        window.location.href = 'aloapp://email-offer-modal';
      } else {
        alert('Please update your app to enjoy the best experience!');
      }
    }
  },
  false
);

// ? [REVISIT] what is this for?
function scrollToTop() {
  globalWindow.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
}
const handleToTop = function () {
  const toTopElement = document.getElementById('toTop');
  if (!toTopElement) return;
  if (globalWindow.innerWidth >= 1346 && globalWindow.scrollY > 2000) {
    toTopElement.style.visibility = 'visible';
    toTopElement.style.opacity = '1';
    toTopElement.addEventListener('click', scrollToTop);
  } else {
    toTopElement.style.opacity = '0';
    toTopElement.style.visibility = 'hidden';
    toTopElement.removeEventListener('click', scrollToTop);
  }
};
const throttledHandleToTop = throttle(handleToTop, 1000);
if (!document.body.classList.contains('template-collection')) {
  globalWindow.addEventListener('scroll', throttledHandleToTop);
}

//@ts-expect-error // ? [REVISIT]
globalWindow.loadYTvideoModal = function (videoURL) {
  const url = videoURL;
  const $videoModal = $('#videoModal');
  $videoModal.on('show.bs.modal', function () {
    $('#ytVideo').attr('src', `${url}?&amp;autoplay=1`);
  });
  $videoModal.on('hide.bs.modal', function () {
    $('#ytVideo').attr('src', '');
  });
};

globalWindow.emailRegex =
  "^[a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$";
// Post email from sign up form w/out reloading the page
globalWindow.postEmail = function (event: Event) {
  event.preventDefault();
  const form = event.target as HTMLFormElement;
  const formId = form.id;
  document.body.classList.add('loading', formId);

  const formData = new FormData(form);
  let signUpStatus: 'success' | 'fail' | 'registered' = 'success';

  const emailCapture = formData.get('contact[email]');
  const tagsCapture = formData.get('contact[tags]');
  if (typeof globalWindow.heap.identify === 'function') {
    globalWindow.heap.identify(emailCapture);
    //override the user's email based on latest form email submission
  }

  const endpoint = `https://${window?.wizUrl}/offer-enroll`;
  const { attentiveEmailSubscriptionEnabled = true } = window.getSiteSettings() || {};

  const requestBody: EmailSignUpRequestBody = {
    email: emailCapture,
    tags: tagsCapture ? tagsCapture.toString().split(',') : [],
    accepts_marketing: 1,
    attentiveEmailSubscription: attentiveEmailSubscriptionEnabled,
  };
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  if (formId === 'contact_form-email') form.style.display = 'none';

  return fetch(endpoint, options)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not OK');
      }
      return response.json();
    })
    .then((data) => {
      if (data.status === 'success') {
        if (data.action === 'new') {
          // user successfully signed up
          document.body.classList.remove('wiz-error');
          document.body.classList.add('email-successfully-signed-up', formId);
          disableFurtherEmailPrompts();
          sendSignUpEventToGA(formId);
          // Push sign up event to Meta via GTM
          const utmSource = InitialUrlSearchParams?.get('utm_source');
          pushEventToGTMDataLayer({
            event: 'email_signup',
            utmSource: utmSource || '',
          });
          form.querySelector('input[type="email"]')?.setAttribute('disabled', 'disabled');
          form.querySelector('button[type="submit"]')?.setAttribute('disabled', 'disabled');
          // End of "Push sign up event to Meta via GTM"
        } else if (data.action === 'updated') {
          // user has already signed up; this is a no-op
          document.body.classList.add('email-already-registered', formId);
          document.body.classList.remove('wiz-error');
          disableFurtherEmailPrompts();
          signUpStatus = 'registered';
        }
        pushEventToGTMDataLayer({ event: 'newsletter_form_submit', form: 'newsletter' });
      } else if (data.status === 'error') {
        // there was an issue with the submitted email
        document.body.classList.add('wiz-error', formId);
        signUpStatus = 'fail';
      }
      document.body.classList.remove('loading');
      return signUpStatus;
    })
    .catch((error) => {
      console.error({ error });
      document.body.classList.add('wiz-error', formId);
      document.body.classList.remove('loading');
      signUpStatus = 'fail';
      return signUpStatus;
    });
};

export const handleAuthForm = (returnUrl: string, location: string, useCustomHero?: boolean) => {
  const mountNode = document.querySelector('[react-render-target="auth-form-container"]');
  const customerInfo = window?.getCustomerInfo?.();

  poll(() => !!mountNode, 5000, 1000).then((isPollingSuccessful) => {
    if (isPollingSuccessful && mountNode && !IS_NATIVE_APP_CLIENT) {
      initAuthForm(returnUrl, 'create-account', { location }, customerInfo?.orderEmail, useCustomHero);
    } else if (isPollingSuccessful && mountNode) {
      const buttonForMobileApp = document.querySelector('a.btn.mobile-app-only');
      mountNode.style.display = 'none';
      buttonForMobileApp && (buttonForMobileApp.style.display = 'block');
    }
  });
};

//@ts-expect-error // ? [REVISIT]
export function sendSignUpEventToGA(formId) {
  const gaConstData = ['send', 'event', 'Signup form'];
  try {
    switch (formId) {
      case 'contact_form':
        ga(...gaConstData, 'Sign in from footer', 'Footer newsletter form');
        break;
      case 'contact_form-email':
        ga(...gaConstData, 'Sign in from pop-up', 'Pop-up newsletter form');
        break;
      case 'contact_form-insta':
        ga(...gaConstData, 'Sign in from insta page', 'Insta newsletter form');
        break;
      default:
        break;
    }
  } catch (e) {
    console.log(e);
  }
}

// Workaround to fix Shopify logout issue due to browser cache: ECOM-1716.
export const handleLogout = () => {
  const href = '/account/logout';
  const logoutButtons: HTMLAnchorElement[] | [] = Array.from(document.querySelectorAll(`a[href^="${href}"]`));
  if (logoutButtons.length) {
    logoutButtons.forEach((btn) =>
      btn.addEventListener('click', async function (e) {
        e.preventDefault();
        manualLogoutTracking();

        // Clear the `cart` cookie on signout if persistent cart is enabled. Shopify removes the welcome gift and birthday gift from the cart present in `cart` cookie when user signout. This is not required in persistent cart flow. That is why we are checking if persistent cart is enabled by checking isDeviceIdSet cookie and clearing the cart cookie.
        if (await shouldUsePersistentCartMode()) {
          setCookie('cart', '', -1);
        }
        fetch(prependLocaleToUrl(btn.href)).then(() => window.location.reload());
      })
    );
  }
};

// Abstracted Heap Events
globalWindow.fireHeapCustomerLoginEvent = fireHeapCustomerLoginEvent;
globalWindow.fireHeapCustomerCreateEvent = fireHeapCustomerCreateEvent;
